import * as React from 'react';
export var VisibilityEventType;
(function (VisibilityEventType) {
    VisibilityEventType["ENTER_COMPLETE"] = "ENTER_COMPLETE";
    VisibilityEventType["EXIT_TOP_COMPLETE"] = "EXIT_TOP_COMPLETE";
    VisibilityEventType["EXIT_BOTTOM_COMPLETE"] = "EXIT_BOTTOM_COMPLETE";
    VisibilityEventType["PARTIAL_TOP"] = "PARTIAL_TOP";
    VisibilityEventType["PARTIAL_BOTTOM"] = "PARTIAL_BOTTOM";
    VisibilityEventType["SPAN_VIEWPORT"] = "SPAN_VIEWPORT";
})(VisibilityEventType || (VisibilityEventType = {}));
/**
 * Wrapper around an IntersectionObserver. This is used to keep
 * all the references in one place, useful for implementing the
 * disabling and destroying of the instances.
 * @param {IntersectionObserverCallback} callback - function called by the IntersectionObserver on new visibility events
 * @param {ObserverOptions} options - option for the IntersectionObserver, see https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
 */
var Observer = /** @class */ (function () {
    function Observer(callback, options) {
        var _this = this;
        this.observe = function (targets) {
            if (targets && targets.length) {
                targets.forEach(function (target) {
                    var ref = target.ref;
                    if (!ref) {
                        return;
                    }
                    if (ref.current) {
                        _this.instance.observe(ref.current);
                    }
                });
            }
        };
        this.isDisabled = function () { return _this.stateStack.length !== 0; };
        this.disable = function () {
            _this.stateStack.push(true);
        };
        this.enable = function () {
            _this.stateStack.pop();
        };
        this.destroy = function () {
            _this.instance.disconnect();
        };
        var rootMargin = options.rootMargin ? options.rootMargin + "px" : undefined;
        this.stateStack = [];
        this.instance = new IntersectionObserver(callback, {
            rootMargin: rootMargin,
            threshold: options.thresholds,
            root: options.root
        });
    }
    return Observer;
}());
var getEventType = function (entry, rootMargin) {
    var margin = rootMargin || 0;
    if (!entry.isIntersecting) {
        var top_1 = entry.rootBounds && entry.rootBounds.top || 0;
        if (entry.boundingClientRect.bottom <= top_1) {
            return VisibilityEventType.EXIT_TOP_COMPLETE;
        }
        return VisibilityEventType.EXIT_BOTTOM_COMPLETE;
    }
    if (entry.intersectionRatio === 1) {
        return VisibilityEventType.ENTER_COMPLETE;
    }
    if (entry.boundingClientRect.top + margin < 0) {
        return VisibilityEventType.PARTIAL_TOP;
    }
    // Adding + margin gives us a little more flexibility for targets of different heights.
    if (entry.rootBounds) {
        var rootHeight = entry.rootBounds.height;
        if (rootHeight && entry.intersectionRect.height >= rootHeight + margin) {
            return VisibilityEventType.SPAN_VIEWPORT;
        }
    }
    return VisibilityEventType.PARTIAL_BOTTOM;
};
export var useVisibility = function (targets, clientCallback, observerOptions) {
    if (observerOptions === void 0) { observerOptions = {}; }
    // No state, else it'll re-render everytime we get new events
    var visibilityEvents = React.useRef({});
    var observer = React.useRef(null);
    // IntersectionObserverCallback factory, returns a function that updates the visibility events map
    var observerCallbackFactory = function (callback, options) { return function (entries) {
        entries.forEach(function (entry) {
            var targetId = entry.target.id;
            if (!targetId) {
                return;
            }
            visibilityEvents.current[targetId] = getEventType(entry, options.rootMargin);
        });
        if (observer.current && !observer.current.isDisabled()) {
            callback(visibilityEvents.current);
        }
    }; };
    React.useEffect(function () {
        if (observer.current) {
            observer.current.destroy();
        }
        var callback = observerCallbackFactory(clientCallback, observerOptions);
        var newObserver = new Observer(callback, observerOptions);
        newObserver.observe(targets);
        observer.current = newObserver;
        // Disconnect on unmount
        return function () { return newObserver.destroy(); };
    }, [clientCallback, observerOptions, targets]);
    return {
        disableObserver: function () { observer.current && observer.current.disable(); },
        enableObserver: function () { observer.current && observer.current.enable(); },
        destroyObserver: function () { observer.current && observer.current.destroy(); }
    };
};
//# sourceMappingURL=use-visibility.js.map